import { useCanRecordOffline } from '@/hooks/desktop/useCanRecordOffline';
import { useIsOfflineConnectivity } from '@/hooks/desktop/useIsOfflineConnectivity';

export interface OfflineGatingState {
  /**
   * Grey out / disable every connectivity-dependent control while offline —
   * the always-visible chrome (left-rail nav, session-list scope tabs +
   * search/filter/sort, session-header Share / Transcribe-dropdown / Context +
   * Transcript tabs / delete / Protocols, note-toolbar dictation + undo/redo +
   * copy, chat composer) plus the recorder-area controls (template pickers,
   * recorder settings, evidence chat).
   *
   * Keyed on connectivity alone (offline + the offline-recording feature in
   * play — see {@link useIsOfflineConnectivity}), NOT on whether this device
   * has a cached identity. The chrome is on screen regardless of whether the
   * recorder is usable, so "disable anything that needs the server" must fire
   * whenever we're offline. (`requiresOnlineLogin` still layers the
   * cached-identity check on top for the recorder's empty-state branch.)
   */
  shouldGateControls: boolean;
  /**
   * Show the "sign in while online first" empty state (FR-003) in place of the
   * recorder: offline with no cached identity, so capture is blocked until the
   * clinician completes one online login on this device.
   */
  requiresOnlineLogin: boolean;
}

/**
 * APP-6672 — offline gating for connectivity-dependent recorder surfaces (eng
 * impl of APP-6175).
 *
 * Splits the offline state into the two UI outcomes the design calls for:
 * grey-out the server-dependent controls whenever we're offline, and (only when
 * the device also can't record offline) block the recorder with the "log in
 * once online first" explainer. Both branches no-op off-desktop, online, or in
 * prod (see {@link useIsOfflineConnectivity}), so callers can read this
 * unconditionally.
 *
 * The global connectivity banner is a separate surface (APP-6669) — this hook
 * does not own it.
 */
export function useOfflineGating(): OfflineGatingState {
  const isOffline = useIsOfflineConnectivity();
  const canRecordOffline = useCanRecordOffline();

  return {
    // Connectivity-only: the always-visible chrome must grey out whenever we're
    // offline, independent of the cached-identity gate (which only decides the
    // recorder's empty-state below). Was previously coupled to
    // `canRecordOffline`, which meant nothing greyed when no identity was cached.
    shouldGateControls: isOffline,
    // `canRecordOffline` is `undefined` while the cached-identity read is still
    // pending — only fire the login wall once we KNOW there's no cached identity
    // (`=== false`), otherwise an offline user who DOES have one would flash the
    // wall before the read resolves.
    requiresOnlineLogin: isOffline && canRecordOffline === false,
  };
}
